// =============================================================================
// Photo capture + on-device watermarking
// =============================================================================
// This is the Encodian replacement (design doc 5.5, section 12).
//
// Flow for a single photo:
//   1. Open the native camera via an <input type="file" capture="environment">
//   2. Load the image bytes into a Canvas
//   3. Stamp date/time, site code, project code, GPS into the bottom-right
//      corner using the Coretel-branded overlay
//   4. Export two copies:
//        - watermarked  (JPEG 0.9 quality, reduced to ≤2048px long edge)
//        - original     (as-uploaded, preserved for audit)
//   5. Request signed PUT URLs from the api-gateway Worker
//   6. Upload both copies directly to R2
//   7. Call /photos/finalise with the metadata
//
// When offline: we cache both blobs in IndexedDB via lib/offline.ts and
// queue a 'photo_finalise' item. The offline-sync loop replays it.
// =============================================================================

import { apiFetch } from "@/lib/supabase";
import { cachePhoto, queueItem } from "@/lib/offline";
import type { GpsFix } from "@/features/gps/useGps";

const WM_MAX_LONG_EDGE = 2048;     // watermarked copy size cap
const WM_JPEG_QUALITY = 0.88;
const BRAND = { accent: "#FF6A1A", dark: "#0D0D0C", light: "#F0EFEA" };

export interface WatermarkContext {
  project_code?: string;
  site_code?: string;
  pack_item_id?: string;
  pack_id?: string;
  employee_name?: string;
}

export interface CapturedPhoto {
  photo_id?: string;             // set after finalise
  watermarked_url?: string;      // set after upload (for preview)
  local_id: string;              // UUID, used while offline
  captured_at: string;
  watermark_data: Record<string, string>;
  size_bytes: number;
  queued_offline: boolean;
}

// -----------------------------------------------------------------------------
// 1. Image → watermarked + original blobs
// -----------------------------------------------------------------------------

export async function watermarkPhoto(
  file: File,
  ctx: WatermarkContext,
  gps: GpsFix | null,
): Promise<{ watermarked: Blob; original: Blob; watermarkText: Record<string, string>; exifStub: Record<string, unknown> }> {
  // Draw into a canvas so we can add the corner stamp
  const img = await readAsImage(file);
  const { canvas, width, height } = fitToCanvas(img, WM_MAX_LONG_EDGE);
  const c = canvas.getContext("2d")!;
  c.drawImage(img, 0, 0, width, height);

  const text = buildWatermarkText(ctx, gps);
  drawWatermark(c, width, height, text);

  const watermarked = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error("watermark_blob_failed"))), "image/jpeg", WM_JPEG_QUALITY);
  });

  return {
    watermarked,
    original: file,
    watermarkText: text,
    exifStub: {
      file_name: file.name,
      original_size_bytes: file.size,
      original_type: file.type,
      captured_at: new Date().toISOString(),
      // Full EXIF extraction requires piexifjs or exifr; stubbed for now.
      // The ORIGINAL file is preserved in R2 with its EXIF intact, so the
      // source of truth is on disk regardless of what we parse here.
    },
  };
}

function readAsImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image_load_failed")); };
    img.src = url;
  });
}

function fitToCanvas(img: HTMLImageElement, maxLongEdge: number): { canvas: HTMLCanvasElement; width: number; height: number } {
  const longEdge = Math.max(img.width, img.height);
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  const width  = Math.round(img.width  * scale);
  const height = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return { canvas, width, height };
}

function buildWatermarkText(ctx: WatermarkContext, gps: GpsFix | null): Record<string, string> {
  const now = new Date();
  return {
    line1: [ctx.project_code, ctx.site_code].filter(Boolean).join(" · "),
    line2: formatAusDateTime(now),
    line3: gps ? formatGpsStamp(gps) : "GPS unavailable",
    line4: ctx.employee_name ?? "",
  };
}

function formatAusDateTime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatGpsStamp(g: GpsFix): string {
  const latDir = g.latitude  >= 0 ? "N" : "S";
  const lngDir = g.longitude >= 0 ? "E" : "W";
  return `${Math.abs(g.latitude).toFixed(5)}°${latDir} ${Math.abs(g.longitude).toFixed(5)}°${lngDir} ±${Math.round(g.accuracy_m)}m`;
}

// -----------------------------------------------------------------------------
// Canvas watermark renderer — bottom-right corner stamp, Coretel-branded
// -----------------------------------------------------------------------------

function drawWatermark(c: CanvasRenderingContext2D, w: number, h: number, text: Record<string, string>) {
  const lines = [text.line1, text.line2, text.line3, text.line4].filter(Boolean);
  const fontSize = Math.max(14, Math.round(w * 0.018));
  const lineHeight = Math.round(fontSize * 1.35);
  const padX = Math.round(fontSize * 0.85);
  const padY = Math.round(fontSize * 0.65);
  const accentW = Math.max(3, Math.round(fontSize * 0.25));

  c.font = `${fontSize}px ui-sans-serif, system-ui, Archivo, sans-serif`;
  c.textBaseline = "top";

  // Measure widest line
  let maxW = 0;
  for (const l of lines) maxW = Math.max(maxW, c.measureText(l).width);

  const boxW = maxW + padX * 2 + accentW;
  const boxH = lines.length * lineHeight + padY * 2;
  const boxX = w - boxW - Math.round(w * 0.018);
  const boxY = h - boxH - Math.round(h * 0.025);

  // Darkened panel
  c.fillStyle = "rgba(13, 13, 12, 0.82)";
  roundedRect(c, boxX, boxY, boxW, boxH, 6);
  c.fill();

  // Accent stripe on the left edge of the panel
  c.fillStyle = BRAND.accent;
  roundedRect(c, boxX, boxY, accentW, boxH, 6);
  c.fill();

  // Text
  c.fillStyle = BRAND.light;
  c.font = `600 ${fontSize}px ui-sans-serif, system-ui, Archivo, sans-serif`;
  c.fillText(lines[0] ?? "", boxX + padX + accentW, boxY + padY);

  c.font = `400 ${fontSize}px ui-sans-serif, system-ui, Archivo, sans-serif`;
  for (let i = 1; i < lines.length; i++) {
    c.fillStyle = i === 2 ? BRAND.accent : "#D0CEC8";
    c.fillText(lines[i], boxX + padX + accentW, boxY + padY + i * lineHeight);
  }
}

function roundedRect(c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.lineTo(x + w - r, y);
  c.quadraticCurveTo(x + w, y, x + w, y + r);
  c.lineTo(x + w, y + h - r);
  c.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  c.lineTo(x + r, y + h);
  c.quadraticCurveTo(x, y + h, x, y + h - r);
  c.lineTo(x, y + r);
  c.quadraticCurveTo(x, y, x + r, y);
  c.closePath();
}

// -----------------------------------------------------------------------------
// 2. Upload pipeline — online: sign → PUT → finalise. Offline: cache & queue.
// -----------------------------------------------------------------------------

export async function uploadPhoto(
  file: File,
  ctx: WatermarkContext,
  gps: GpsFix | null,
): Promise<CapturedPhoto> {
  const { watermarked, original, watermarkText, exifStub } = await watermarkPhoto(file, ctx, gps);
  const local_id = cryptoRandomId();
  const captured_at = new Date().toISOString();

  if (!navigator.onLine) {
    // Cache both blobs, queue the finalise op
    await cachePhoto(`${local_id}_wm`,   watermarked, { ...watermarkText, kind: "watermarked" });
    await cachePhoto(`${local_id}_orig`, original,   { kind: "original", ...exifStub });
    await queueItem("photo_finalise", {
      local_id,
      pack_item_id: ctx.pack_item_id,
      pack_id: ctx.pack_id,
      watermark_data: watermarkText,
      exif: exifStub,
      captured_at,
      gps_lat: gps?.latitude,
      gps_lng: gps?.longitude,
      gps_accuracy_m: gps?.accuracy_m,
    });
    return {
      local_id, captured_at, watermark_data: watermarkText,
      size_bytes: watermarked.size, queued_offline: true,
    };
  }

  // Online path: two uploads + finalise
  const baseKey = `projects/${ctx.project_code ?? "unassigned"}/acceptance-packs/${ctx.pack_id ?? "adhoc"}/${local_id}`;
  const wmKey   = `${baseKey}.jpg`;
  const origKey = `${baseKey}_original${extensionOf(file.name) ?? ""}`;

  const wmUp   = await signAndPut(wmKey,   watermarked, "image/jpeg");
  const origUp = await signAndPut(origKey, original,   file.type || "image/jpeg");

  const finaliseRes = await apiFetch("/photos/finalise", {
    method: "POST",
    body: JSON.stringify({
      pack_item_id: ctx.pack_item_id,
      pack_id: ctx.pack_id,
      watermarked: { object_key: wmUp.object_key,   size_bytes: watermarked.size },
      original:    { object_key: origUp.object_key, size_bytes: original.size },
      captured_at,
      gps_lat: gps?.latitude,
      gps_lng: gps?.longitude,
      gps_accuracy_m: gps?.accuracy_m,
      watermark_data: watermarkText,
      exif: exifStub,
      device_info: { ua: navigator.userAgent, platform: navigator.platform, online: true },
    }),
  });

  if (!finaliseRes.ok) {
    throw new Error(`finalise_failed_${finaliseRes.status}`);
  }
  const finalData = await finaliseRes.json();

  return {
    photo_id: finalData.photo_id,
    watermarked_url: URL.createObjectURL(watermarked),
    local_id, captured_at,
    watermark_data: watermarkText,
    size_bytes: watermarked.size,
    queued_offline: false,
  };
}

async function signAndPut(object_key: string, blob: Blob, contentType: string): Promise<{ object_key: string }> {
  const signRes = await apiFetch("/sign-upload", {
    method: "POST",
    body: JSON.stringify({
      bucket: "coretel-prod",
      object_key,
      content_type: contentType,
      expires_in_seconds: 900,
    }),
  });
  if (!signRes.ok) throw new Error(`sign_upload_${signRes.status}`);
  const { upload_url } = await signRes.json();

  const putRes = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: blob,
  });
  if (!putRes.ok) throw new Error(`r2_put_${putRes.status}`);

  return { object_key };
}

function extensionOf(name: string): string | null {
  const i = name.lastIndexOf(".");
  return i >= 0 ? name.substring(i) : null;
}

function cryptoRandomId(): string {
  return (crypto.randomUUID?.() ?? String(Date.now()) + Math.random().toString(36).slice(2)).replace(/[^a-z0-9-]/gi, "");
}
